import { useEffect, useState } from 'react';

import { Card, Col, Form, Input, Modal, Radio, Row } from 'antd';
import { useGetMediaOfferGroupByIdQuery } from 'services';
import MediaContentForm from 'components/SendItems/AddMediaContent/MediaContent/MediaContentForm';
import { useMediaActionContext } from 'components/SendItems/AddMediaContent/Context/MediaActioncontext';
import { isAfterCopyrightDecider, MEDIA_OFFER_DECIDER } from 'hooks/useMediaOfferPermission';
import { MEDIA_MODAL_PROPS, MEDIA_OFFER_FORM_FIELDS, MEDIA_OFFER_VALIDATE_FIELDS, mapVersionToForm } from 'components/SendItems/AddMediaContent/helpers';

import MediaEvaluationTimeline from './MediaEvaluationTimeline';
import './style.less';

/**
 * Bir BE karar objesini (`MediaOfferDecision`) timeline'ın beklediği `MediaEvaluation` şekline çevirir.
 * @param {Object} decision - `currentVersion.decisions[]` elemanı
 * @returns {{ id: number|string, unit: string, note: string, person: string|null, date: string }}
 */
const mapDecisionToEvaluation = (decision) => ({
  id: decision.id,
  unit: decision.decidedBy,
  note: decision.decisionNote,
  // Tip opsiyonel: limitedEdit (YM) yalnızca not bırakabilir → timeline rozeti gizler.
  decisionType: decision.decisionType ?? null,
  // decidedByUser bir User objesi; eski kayıtlarda null olabilir → timeline "-" basar.
  person: decision.decidedByUser?.fullName ?? null,
  date: decision.createdAt,
});

const { TextArea } = Input;

/**
 * Karar seçenekleri. Renklendirme `style.less`'teki `--{modifier}` sınıflarıyla yapılır
 * (etiket rengi her zaman sabit; seçili olanın kutusu + noktası ilgili renge boyanır).
 */
const DECISION_OPTIONS = [
  { value: 'approved', label: 'Uygundur', modifier: 'approved' },
  { value: 'not_approved', label: 'Uygun Değildir', modifier: 'not-approved' },
];

/**
 * @description Karar kartı başlığı → aktif birime (`currentDecider`) göre. Modal hem İlim Heyeti
 * hem telif sonrası birimleri tarafından görüldüğü için başlık sabit olamaz: heyet "Heyet Notları"
 * görürken telif sonrası birimleri kendi adını görür. Eşleşme yoksa nötr başlık.
 *
 * `DecisionMakerBadge`'in etiket haritasından AYRI tutulur: oradaki etiketler kompakt tablo rozetidir
 * (ör. "İH - Madde Kontrol"); burada ise heading olarak doğal okunan kopya gerekir (ör. "Heyet Notları").
 * @type {Record<string, string>}
 */
const DECISION_CARD_TITLE = {
  [MEDIA_OFFER_DECIDER.ihControl]: 'Heyet Notları',
  [MEDIA_OFFER_DECIDER.ihControlTranslate]: 'Heyet Notları',
  [MEDIA_OFFER_DECIDER.supervisor]: 'Madde Sorumlusu Notları',
  [MEDIA_OFFER_DECIDER.postOrderMediaOfferControl]: 'Medya Teklifi Kontrolü Notları',
  [MEDIA_OFFER_DECIDER.postOrderEuControl]: 'Ansiklopedi Birimi Notları',
  [MEDIA_OFFER_DECIDER.postOrderOwnerCommitteeControl]: 'Heyet Notları',
  [MEDIA_OFFER_DECIDER.postOrderSupervisor]: 'Madde Sorumlusu Notları',
  [MEDIA_OFFER_DECIDER.postOrderPubControl]: 'Yayın Müdürlüğü Notları',
  [MEDIA_OFFER_DECIDER.postOrderCitationSourceRedaction]: 'Atıf-Kaynaklar Redaksiyonu Notları',
  [MEDIA_OFFER_DECIDER.postOrderTranslationTextRedaction]: 'Çeviri-Yazı Redaksiyonu Notları',
  [MEDIA_OFFER_DECIDER.postOrderOrthographyRedaction]: 'İmla Redaksiyonu Notları',
  [MEDIA_OFFER_DECIDER.postOrderProofreadingControl]: 'Editöryal Son Okuma Notları',
};

/**
 * Heyet/TS rollerinin bir medya teklifini inceleyip (alan düzenleme) ve karar verdiği modal.
 *
 * Option C (Karar 9): Figma'daki tek "Kaydet ve Kapat" aksiyonu, context'teki
 * `handleReviseAndDecide` ile revize (form kirliyse) + karar/not (seçiliyse) akışını ardışık
 * yürütür. Karar alanları (radyo + not) form'dan ayrı local state'te tutulur ki revize
 * gövdesine sızmasın; default karar konmaz (kaza ile onay önlenir). İptal → hiçbir istek gitmez.
 *
 * @param {Object} props
 * @param {number|string} props.mediaID - Güncel versiyonun (currentVersion) id'si; revize/karar hedefi
 * @param {string|null} [props.currentDecider=null] - Maddenin aktif decider rolü
 *   (`useMediaOfferPermission(offer).activeDecider`). Değerlendirme timeline'ı yalnız bu decider telif
 *   sonrası bir aşamaya aitken gösterilir (telif öncesi heyet görüş ekranlarında gizli kalır).
 *   `null` ise (aktif karar aşaması olmayan statü — yalnız admin bu durumda modalı düzenleme modunda
 *   açabilir) karar kartı hiç render edilmez: modal sadece içerik düzeltmesi yapar.
 * @param {boolean} [props.readOnly=false] - true ise salt-görüntüleme detay modu: başlık "Medya Detayı",
 *   footer (Kaydet ve Kapat) kaldırılır, form alanları salt-okunur ve karar kartı (radyo + değerlendirme)
 *   gizlenir. Yönetme yetkisi olmayan izleyiciler için (ArticleOrderMedia read-only fallback) kullanılır.
 *   Değerlendirme timeline'ı `currentDecider` kuralıyla (telif sonrası) aynen görünür.
 * @param {string|null} [props.actingAs=null] - Current_user'ın bu maddede hangi birim adına işlem
 *   yaptığı (`useMediaOfferPermission(offer).actingAs`). Karar/not kartının BAŞLIĞI buradan gelir —
 *   notu yazan kişinin kendi birimini görmesi gerekir. Yoksa `currentDecider`'a düşer.
 * @param {boolean} [props.canDecide=false] - Uygundur / Uygun Değildir seçimi yapılabilir mi
 *   (`useMediaOfferPermission(offer).canDecide`). `false` iken kartta yalnız değerlendirme notu kalır.
 * @param {boolean} [props.limitedEdit=false] - true ise düzenleme modu açık ama yalnız Alt Yazı,
 *   Kaynak ve Link editable (diğer alanlar disabled); YM İmla/Editöryal redaksiyonu için. Uygundur/uygun
 *   değildir radyosu gizlenir; değerlendirme notu + "Kaydet ve Kapat" çalışır. `readOnly` ile birlikte
 *   verilmez; `readOnly` önceliklidir (her şeyi kapatır).
 */
function SupervisorEditMediaContent({ mediaID, currentDecider = null, actingAs = null, canDecide = false, readOnly = false, limitedEdit = false }) {
  const { func, states } = useMediaActionContext();
  const { groupId } = states.editTrigger;

  // Değerlendirme timeline'ı yalnız telif sonrası aşamalarda görünür (design gereği).
  const showEvaluations = isAfterCopyrightDecider(currentDecider);
  // Karar/not kartı bir AŞAMAYA aittir: aktif decider yoksa kart render edilmez. `limitedEdit`
  // redaktörlerde tip seçimi yok (`canSelectDecision`); yalnız değerlendirme notu kalır.
  const showDecisionCard = !readOnly && Boolean(currentDecider);
  const canSelectDecision = showDecisionCard && canDecide;
  // Başlık, notu YAZAN birime göre kurulur (`actingAs`); yoksa aşamanın birimine düşer.
  const cardTitle = DECISION_CARD_TITLE[actingAs ?? currentDecider] || (canSelectDecision ? 'Değerlendirme ve Karar' : 'Değerlendirme');

  // Grup detayı groupId ile çekilir; form currentVersion'dan, revize/karar hedefi mediaID (versiyon id'si)
  const { data } = useGetMediaOfferGroupByIdQuery({ id: groupId }, { skip: !groupId });

  // Değerlendirmeler: güncel versiyonun TÜM kararları. API eskiden yeniye verir, timeline
  // yeniden eskiye ister → önce kopyalayıp ters çevir (reverse mutasyonu API cache'ini bozmasın),
  // sonra map'le. Boş nota `MediaEvaluationTimeline` zaten "-" basar.
  const decisions = data?.currentVersion?.decisions ?? [];
  const evaluations = decisions.slice().reverse().map(mapDecisionToEvaluation);
  const [form] = Form.useForm();

  // Karar alanları form dışında — yoksa form.getFieldsValue() ile revize gövdesine sızar
  const [decisionType, setDecisionType] = useState();
  const [decisionNote, setDecisionNote] = useState('');

  const resetAndClose = () => {
    form.resetFields(MEDIA_OFFER_FORM_FIELDS);
    setDecisionType(undefined);
    setDecisionNote('');
    func.handleEditStateTrigger({ trigger: false, mediaID: null, groupId: null });
  };

  // Tek "Kaydet ve Kapat": alan düzenlemesi (kirliyse) + karar/not (seçiliyse/doluysa) tek aksiyonda
  const handleSave = () => {
    form.validateFields(MEDIA_OFFER_VALIDATE_FIELDS).then(() => {
      func.handleReviseAndDecide({
        versionId: mediaID,
        isDirty: form.isFieldsTouched(MEDIA_OFFER_FORM_FIELDS),
        values: form.getFieldsValue(),
        // limitedEdit: tip asla gönderilmez (radyolar gizli; state sızmasın)
        decisionType: canSelectDecision ? decisionType : undefined,
        decisionNote,
        groupId,
      });
      resetAndClose();
    });
  };

  useEffect(() => {
    // trigger deps: aynı medya tekrar açıldığında data referansı değişmediği için
    // açılışta (trigger true) yeniden doldurulmalı (#21)
    const version = data?.currentVersion;
    if (version && states.editTrigger.trigger) {
      // mapVersionToForm grubun tamamını alır: mediaType grup düzeyinde, geri kalanı currentVersion'da
      form.setFieldsValue(mapVersionToForm(data));
      // Karar alanları (radyo + not) yalnız SON KARAR mevcut aşamaya (currentDecider) aitse önden dolar.
      // Başka bir birimin kararı (ör. telif sonrasındayken önceki İlim Heyeti kararı) prefill EDİLMEZ:
      // o değerlendirme yalnız üstteki timeline'da salt-okunur görünür; bu form kullanıcının KENDİ
      // kararını/notunu sıfırdan yazacağı yerdir. Böylece başkasının notu/kararı kendi alanımıza karışmaz.
      const lastDecision = version.lastDecision;
      const isOwnDecision = Boolean(currentDecider) && lastDecision?.decidedBy === currentDecider;
      // limitedEdit: tip seçimi yok → prefill etme (gönderimde de tip sızmaz). Not doldurulabilir.
      setDecisionType(isOwnDecision && !limitedEdit ? lastDecision.decisionType : undefined);
      setDecisionNote(isOwnDecision ? lastDecision.decisionNote || '' : '');
    }
  }, [data, states.editTrigger.trigger, form, currentDecider, limitedEdit]);

  return (
    <Modal
      {...MEDIA_MODAL_PROPS}
      title={readOnly ? 'Medya Detayı' : limitedEdit ? 'Altyazı / Kaynak / Link' : 'Medyayı Düzenle'}
      okText="Kaydet ve Kapat"
      open={states.editTrigger.trigger}
      onOk={handleSave}
      onCancel={resetAndClose}
      // Read-only detayda kaydet/karar aksiyonu yok → footer tamamen kaldırılır; kapatma sağ-üst (X) ile.
      footer={readOnly ? null : undefined}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <MediaContentForm
          readOnlyMediaType
          readOnly={readOnly}
          limitedEdit={limitedEdit}
        />
      </Form>
      {/* Telif sonrası: bu gruba yapılmış değerlendirmelerin zaman çizelgesi (karar formunun üstünde) */}
      {showEvaluations && <MediaEvaluationTimeline evaluations={evaluations} />}
      {/* Karar/not kartı yalnız işlem yapan decider için; read-only detayda ve aktif aşama yokken
          gizli. limitedEdit'te radyolar yok, yalnız değerlendirme notu. */}
      {showDecisionCard && (
        <Card
          className="gx-mt-3"
          title={cardTitle}
        >
          {canSelectDecision && (
            <Radio.Group
              className="gx-w-100 gx-mb-4"
              value={decisionType}
              onChange={(e) => setDecisionType(e.target.value)}
            >
              <Row gutter={24}>
                {DECISION_OPTIONS.map((option) => {
                  const isSelected = decisionType === option.value;
                  return (
                    <Col
                      span={12}
                      key={option.value}
                    >
                      <div
                        role="presentation"
                        onClick={() => setDecisionType(option.value)}
                        className={`media-decision-option media-decision-option--${option.modifier}${isSelected ? ' media-decision-option--selected' : ''}`}
                      >
                        <Radio value={option.value}>{option.label}</Radio>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </Radio.Group>
          )}
          <div>
            <label className="gx-d-block gx-mb-2">Değerlendirme</label>
            <TextArea
              rows={7}
              value={decisionNote}
              onChange={(e) => setDecisionNote(e.target.value)}
              placeholder="Değerlendirmenizi buraya giriniz..."
            />
          </div>
        </Card>
      )}
    </Modal>
  );
}
export default SupervisorEditMediaContent;
